import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import ErrorMessage from "../ErrorMessage";
import { AuthLogin, EyeCloseIcon, EyeIcon, Tick } from "asset/icons";
import Input from "component/form/Input";
import { handleCheckEmail, handleSendVerificationEmail } from "api/fetchers";
import { LOGOUT_USER } from "store/action";
import { useDispatch, useSelector } from "react-redux";
import { trackOnboardingStep } from "utilities/googleAnalytics/trackOnboardingStep";
import Loaders from "component/loader";
import { toast } from "react-toastify";

const SignUp = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const selector = useSelector((state) => state.users);

    const [formData, setFormData] = useState({
        email: "",
        firstName: "",
        lastName: "",
        password: "",
    });

    const [showPassword, setShowPassword] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [loading, setLoading] = useState(false);

    const [passwordValidations, setPasswordValidations] = useState({
        uppercase: false,
        lowercase: false,
        number: false,
        specialChar: false,
        minLength: false,
    });

    const validatePassword = (password) => {
        setPasswordValidations({
            uppercase: /[A-Z]/.test(password),
            lowercase: /[a-z]/.test(password),
            number: /\d/.test(password),
            specialChar: /[!@#$%^&*(),.?":{}|<>\-_]/.test(password),
            minLength: password.length >= 8,
        });
    };

    const handleChange = (value, name) => {
        setFormData((prev) => ({ ...prev, [name]: value }));
        if (name === "password") validatePassword(value);
    };

    // Helpers
    const getErrorMsg = (err) =>
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        "Something went wrong. Please try again.";

    const fail = (msg) => {
        setErrorMessage(msg);
        toast.error(msg, { toastId: "signup-error" });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const { email, password, firstName, lastName } = formData;

        if (!email || !password || !firstName || !lastName) {
            return fail("All fields are required.");
        }
        if (!Object.values(passwordValidations).every(Boolean)) {
            return fail("Password does not meet all the requirements.");
        }

        setLoading(true);
        setErrorMessage("");

        try {
            if (selector?.isLoggedIn) {
                LOGOUT_USER("", dispatch);
            }

            const res = await handleCheckEmail(email);
            if (res?.statusCode !== 200) {
                return fail(res?.message || "Unable to verify email. Please try again.");
            }

            await handleSendVerificationEmail(email, firstName);
            toast.success("Verification email sent. Check your inbox.");

            trackOnboardingStep("onboarding_start", email);

            navigate("/auth/verify-email", {
                state: { email, password, firstName, lastName, flow: "signup" },
            });
        } catch (err) {
            fail(getErrorMsg(err));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="w-full max-w-[500px] xl:max-w-[650px] !py-4 mt-10 lg:mt-0 md:mt-0 !px-3 bg-white rounded-2xl border border-[#EBEDF9]">
            <AuthLogin className="h-24 w-24" />

            <div className="md:px-6 pb-8">
                <div className="mb-6">
                    <h2 className="text-3xl font-semibold text-[#101828]">Get started today</h2>
                    <p className="text-[#667085] text-sm">
                        Create an account or{" "}
                        <span
                            onClick={() => navigate("/auth/signin")}
                            className="text-[#6f00ff] font-medium cursor-pointer"
                        >
                            Sign In
                        </span>
                    </p>
                </div>
                {errorMessage && <ErrorMessage message={errorMessage} />}
                <form onSubmit={handleSubmit} className="space-y-4 w-full">
                    <Input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={(value) => handleChange(value, "email")}
                        placeholder="Enter your email"
                    />
                    <Input
                        type="text"
                        name="firstName"
                        value={formData.firstName}
                        onChange={(value) => handleChange(value, "firstName")}
                        placeholder="Enter your first name"
                    />
                    <Input
                        type="text"
                        name="lastName"
                        value={formData.lastName}
                        onChange={(value) => handleChange(value, "lastName")}
                        placeholder="Enter your last name"
                    />

                    <div className="relative">
                        <Input
                            type={showPassword ? "text" : "password"}
                            name="password"
                            value={formData.password}
                            onChange={(value) => handleChange(value, "password")}
                            placeholder="Enter your password"
                        />
                        <button
                            type="button"
                            className="absolute inset-y-0 right-3 flex items-center text-[#667085]"
                            onClick={() => setShowPassword((prev) => !prev)}
                        >
                            {showPassword ? (
                                <EyeCloseIcon className="w-6 h-6" />
                            ) : (
                                <EyeIcon className="w-6 h-6" />
                            )}
                        </button>
                    </div>

                    {/* Password rules */}
                    <div className="flex flex-wrap gap-2">
                        {Object.entries(passwordValidations).map(([key, valid]) => {
                            const labelMap = {
                                uppercase: "Uppercase",
                                lowercase: "Lowercase",
                                number: "Number",
                                specialChar: "Special character",
                                minLength: "8 characters",
                            };
                            return (
                                <span
                                    key={key}
                                    className={`px-2 py-1 text-xs flex rounded-full ${
                                        valid
                                            ? "bg-[#DCFDDB] text-[#1DCB1C] border border-[#1DCB1C]"
                                            : "bg-[#EFF0FB] text-[#475467]"
                                    }`}
                                >
                                    {valid && <Tick className="mr-1" />} {labelMap[key]}
                                </span>
                            );
                        })}
                    </div>

                    <p className="text-sm text-[#667085] mt-2">
                        By creating an account, you agree to our{" "}
                        <span className="text-[#6f00ff] font-medium">Terms of Service</span> and{" "}
                        <span className="text-[#6f00ff] font-medium">Privacy Policy</span>
                    </p>

                    <div className="flex justify-end">
                        <button
                            type="submit"
                            disabled={
                                loading ||
                                !formData.email ||
                                !formData.firstName ||
                                !formData.lastName ||
                                !formData.password ||
                                !Object.values(passwordValidations).every(Boolean)
                            }
                            className={`px-6 py-3 rounded-lg text-white text-sm font-semibold ${
                                loading ||
                                !formData.email ||
                                !formData.firstName ||
                                !formData.lastName ||
                                !formData.password ||
                                !Object.values(passwordValidations).every(Boolean)
                                    ? "bg-[#E9D7FE] opacity-50 cursor-not-allowed"
                                    : "bg-[#6f00ff] hover:bg-[#6B46C1]"
                            }`}
                        >
                            {loading ? <Loaders /> : "Create account"}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default SignUp;
